import { computed, toValue } from 'vue';
import type {
  PricingChange,
  PricingChangeStatus,
} from '@cutr/constants/pricing-change';
import { queryOptions, useMutation, useQueryClient } from '@tanstack/vue-query';
import {
  createPricingChange,
  deletePricingChange,
  getPricingChangesForVariant,
  reviewPricingChange,
  setPricingChangeStatus,
  updatePricingChange,
} from '@/api/pricingChanges';
import { pricingChangeKeys } from './keys';
import { useScopedQuery } from './shared/useScopedQuery';
import type { MaybeId, QueryFlags, WithVariant } from './shared/types';

export function pricingChangesListOptions(variantId: string) {
  return queryOptions({
    queryKey: pricingChangeKeys.list(variantId),
    queryFn: ({ signal }) => getPricingChangesForVariant(variantId, signal),
  });
}

export function usePricingChangesQuery(variantId: MaybeId, flags?: QueryFlags) {
  const query = useScopedQuery(variantId, pricingChangesListOptions, flags);

  return { pricingChanges: computed(() => query.data.value ?? []), query };
}

export type SetStatusInput = {
  id: string;
  status: PricingChangeStatus;
  declinedReason?: string;
};

export type ReviewInput = {
  id: string;
  payload: {
    acceptedItemIds: string[];
    declinedItems: { id: string; declinedReason: string }[];
  };
};

// Every mutation here invalidates and none of them write the response into
// the cache:
// (1) the list is role-shaped (getPricingChangesForOrderVariant)
// while the single entity a POST returns (getPricingChange) carries none of that;
// (2) accepting a change with admin-declined items splits it into two new rows and archives the one
// posted to (#splitPricingChange);
// (3) accepting rewrites quotes and offers server-side (applyPricingChangeItems).
// The list is a handful of rows, so refetching it on every write is cheap.
export function usePricingChangeMutations(variantId: MaybeId) {
  const queryClient = useQueryClient();

  const invalidate = (orderVariantId: string) =>
    queryClient.invalidateQueries({
      queryKey: pricingChangeKeys.all(orderVariantId),
    });

  const save = useMutation({
    mutationFn: ({
      pricingChange,
    }: WithVariant<{ pricingChange: PricingChange }>) =>
      pricingChange.id === 'new'
        ? createPricingChange(pricingChange)
        : updatePricingChange(pricingChange.id, pricingChange),
    onSettled: (_data, _error, { orderVariantId }) =>
      invalidate(orderVariantId),
  });

  const setStatus = useMutation({
    mutationFn: ({ id, status, declinedReason }: WithVariant<SetStatusInput>) =>
      setPricingChangeStatus(
        id,
        status,
        declinedReason ? { declinedReason } : {}
      ),
    onSettled: (_data, _error, { orderVariantId }) =>
      invalidate(orderVariantId),
  });

  const review = useMutation({
    mutationFn: ({ id, payload }: WithVariant<ReviewInput>) =>
      reviewPricingChange(id, payload),
    onSettled: (_data, _error, { orderVariantId }) =>
      invalidate(orderVariantId),
  });

  const remove = useMutation({
    mutationFn: ({ id }: WithVariant<{ id: string }>) =>
      deletePricingChange(id),
    onSettled: (_data, _error, { orderVariantId }) =>
      invalidate(orderVariantId),
  });

  // Read when the call starts rather than when it settles: a variant switch
  // while the request is in flight must still invalidate the list it wrote to.
  const orderVariantId = () => toValue(variantId) ?? '';

  return {
    savePricingChange: (pricingChange: PricingChange) =>
      save.mutateAsync({ orderVariantId: orderVariantId(), pricingChange }),
    setPricingChangeStatus: (input: SetStatusInput) =>
      setStatus.mutateAsync({ ...input, orderVariantId: orderVariantId() }),
    reviewPricingChange: (input: ReviewInput) =>
      review.mutateAsync({ ...input, orderVariantId: orderVariantId() }),
    deletePricingChange: (id: string) =>
      remove.mutateAsync({ id, orderVariantId: orderVariantId() }),
  };
}
